import { useEffect, useMemo, useCallback } from "react";
import { useNavigate, useLocation, Navigate } from "react-router-dom";
import { useProjects } from "@/features/projects/hooks/useProjects";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/shared/integrations/supabase/client";
import { selectWithSurveySource } from "@/shared/integrations/supabase/surveySource";
import { useAuth } from "@/shared/hooks/useAuth";
import { firebaseAuth } from "@/shared/integrations/firebase/client";
import { Button } from "@/shared/ui/button";
import { Badge } from "@/shared/ui/badge";
import {
  ChevronRight,
  ClipboardList,
  Building2,
  FileSpreadsheet,
  Home,
  ArrowLeft,
  ExternalLink,
} from "lucide-react";
import { withAppBasePath } from "@/shared/lib/appBasePath";
import type { ProjectWithProperties } from "@/features/projects/services/projectsAdapter";
import type { PropertyAttributeRow } from "@/features/properties/types/propertyAttributes";
import { ProjectSelector } from "./ProjectSelector";
import { PropertySelector } from "./PropertySelector";
import { SurveySelector } from "./SurveySelector";
import { UploadTemplateButton } from "./UploadTemplateButton";
import { SiteSurveyEditorView } from "./SiteSurveyEditorView";
import { PropertyFieldsEditor } from "./PropertyFieldsEditor";
import { AssetsEditorSection } from "./AssetsEditorSection";
import { useSiteSurveyEditor } from "./hooks/useSiteSurveyEditor";
import { useTemplateOptions } from "./hooks/useTemplateOptions";
import { useSurveyCatalogCrosswalk } from "./hooks/useSurveyCatalogCrosswalk";
import { usePropertySurveyAssets } from "./hooks/usePropertySurveyAssets";
import { useExcelTemplatePicker } from "./hooks/useExcelTemplatePicker";
import { resolveSurveyAttributes } from "@/features/site-survey-editor/services/resolveSurveyAttributes";
import {
  collectTemplateCellWrites,
  safeFilename,
} from "@/features/site-survey-editor/services/exportSurveyToExcelTemplate";
import { buildTemplatedSurveysBundle } from "@/features/site-survey-editor/services/exportAllSurveysToExcelTemplate";
import { useDownloadManager } from "@/features/exports/services/DownloadManagerContext";

// Use untyped alias since property_attributes isn't in generated types
const db = supabase as unknown as any;

/** Only these Firebase UIDs may access the site-survey route */
import { isAdminUser } from "@/shared/lib/adminUids";

type Step = "project" | "property" | "survey" | "editor";

interface SelectedProperty {
  id: string;
  name: string;
  address?: string;
  city?: string;
  state?: string;
}

/**
 * URL structure:
 *   /site-surveys                          → Step 1: pick project
 *   /site-surveys/:projectId               → Step 2: pick property
 *   /site-surveys/:projectId/:propertyId   → Step 3: pick survey
 *   /site-surveys/:projectId/:propertyId/:surveyId → Step 4: editor
 */
function parseUrlSegments(pathname: string) {
  const base = "/site-surveys";
  const rest = pathname.startsWith(base) ? pathname.slice(base.length) : "";
  const parts = rest.split("/").filter(Boolean);
  return {
    projectId: parts[0] || null,
    propertyId: parts[1] || null,
    surveyId: parts[2] || null,
  };
}

export default function SiteSurveyEditor() {
  const { loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Wait for auth to load before gating
  if (authLoading) return null;

  // Gate: only admin users may access this route
  const firebaseUid = firebaseAuth?.currentUser?.uid;
  if (!firebaseUid || !isAdminUser(firebaseUid)) {
    return <Navigate to="/dashboard" replace />;
  }

  const { projects, loading, reload } = useProjects();

  // Derive IDs from the URL
  const urlIds = useMemo(
    () => parseUrlSegments(location.pathname),
    [location.pathname]
  );

  // Determine the current step from URL depth
  const step: Step = urlIds.surveyId
    ? "editor"
    : urlIds.propertyId
    ? "survey"
    : urlIds.projectId
    ? "property"
    : "project";

  // ── Resolve entities from URL IDs ──────────────────────

  // Project (from the already-loaded projects list)
  const selectedProject: ProjectWithProperties | null = useMemo(() => {
    if (!urlIds.projectId || !projects.length) return null;
    return projects.find((p) => p.id === urlIds.projectId) ?? null;
  }, [urlIds.projectId, projects]);

  // Property (from the selected project's properties)
  const selectedProperty: SelectedProperty | null = useMemo(() => {
    if (!urlIds.propertyId || !selectedProject) return null;
    const prop = selectedProject.properties.find(
      (p: any) => p.id === urlIds.propertyId
    );
    return prop
      ? {
          id: prop.id,
          name: prop.name || prop.address || "Unnamed",
          address: prop.address,
          city: prop.city,
          state: prop.state,
        }
      : null;
  }, [urlIds.propertyId, selectedProject]);

  // Survey (fetch from Supabase if surveyId is in the URL)
  const { data: selectedSurvey } = useQuery({
    queryKey: ["property-attribute", urlIds.surveyId],
    queryFn: async (): Promise<PropertyAttributeRow | null> => {
      const { data, error } = await db
        .from("property_attributes")
        .select(
          await selectWithSurveySource(
            "id, property_id, captured_at, data, prod_data, normalization_status, processing_events, last_attempt_at, attempt_count, ref_id"
          )
        )
        .eq("id", urlIds.surveyId)
        .single();
      if (error) throw error;
      return (data ?? null) as PropertyAttributeRow | null;
    },
    enabled: !!urlIds.surveyId,
    staleTime: 30_000,
  });

  // ── Template options (fetched from attribute_templates) ─

  const { optionsMap, requiredSet, templateFields, excelCellMappings, excelDefaultSheet } =
    useTemplateOptions(urlIds.projectId);

  // Step 2: export every property's latest survey into copies of a picked
  // Excel template, bundled as a zip through the download manager.
  const { enqueue } = useDownloadManager();
  const { pick: pickBulkExcelTemplate, dialog: bulkExcelSheetDialog } = useExcelTemplatePicker(
    (source, choice) => {
      // Bulk export is local-template only (no getServerTemplateUrl passed).
      if (source.kind !== "local") return;
      const file = source.file;
      const project = selectedProject;
      if (!project) return;
      if (
        collectTemplateCellWrites(templateFields, new Map()).mapped === 0 &&
        excelCellMappings.length === 0
      ) {
        alert("This project's template has no Excel cell mappings (excelTemplateCell).");
        return;
      }
      const uploading = choice.destination === "upload";
      void file.arrayBuffer().then((templateBuf) => {
        enqueue({
          label: uploading
            ? `Survey Excel uploads to RI projects — ${project.name}`
            : `Survey Excel exports — ${project.name}`,
          filename: uploading
            ? `${safeFilename(project.name)} - upload report.txt`
            : `${safeFilename(project.name)} - survey excel exports.zip`,
          build: (ctx) =>
            buildTemplatedSurveysBundle(
              {
                templateBuf,
                templateFields,
                properties: project.properties,
                projectId: project.id,
                templateExt: /\.xlsm$/i.test(file.name) ? "xlsm" : "xlsx",
                destination: choice.destination,
                excelCellMappings,
                defaultSheet: choice.defaultSheet ?? excelDefaultSheet,
              },
              ctx
            ),
        });
      });
    },
    { templateDefaultSheet: excelDefaultSheet }
  );

  // Survey catalog crosswalk (ref_id → question_id) for prod_data writes on save.
  const { data: catalog } = useSurveyCatalogCrosswalk();

  // ── Property assets (for displaying alongside survey questions) ─

  const { data: assetsByCategory = {} } = usePropertySurveyAssets(urlIds.propertyId);

  // ── Editor hook ────────────────────────────────────────

  const {
    fields,
    isDirty,
    isSaving,
    initializeFields,
    updateFieldValue,
    updateFieldMetadata,
    saveNow,
  } = useSiteSurveyEditor(selectedSurvey?.id, catalog);

  // Initialize fields when the survey is loaded. Resolve via the gate: render the
  // self-describing `prod_data` when `survey_source='prod_data'`, else the legacy
  // `data` tree.
  useEffect(() => {
    if (!selectedSurvey) return;
    const dataTree =
      typeof selectedSurvey.data === "string"
        ? JSON.parse(selectedSurvey.data)
        : (selectedSurvey.data ?? []);
    const { fields: resolved } = resolveSurveyAttributes({
      surveySource: (selectedSurvey as { survey_source?: string | null }).survey_source ?? null,
      prodData: (selectedSurvey as { prod_data?: Record<string, unknown> | null }).prod_data ?? null,
      data: dataTree,
    });
    initializeFields(resolved);
  }, [selectedSurvey, initializeFields]);

  // ── Navigation helpers (update URL) ────────────────────

  const handleSelectProject = useCallback(
    (project: ProjectWithProperties) => {
      navigate(`/site-surveys/${project.id}`);
    },
    [navigate]
  );

  const handleSelectProperty = useCallback(
    (property: SelectedProperty) => {
      navigate(`/site-surveys/${urlIds.projectId}/${property.id}`);
    },
    [navigate, urlIds.projectId]
  );

  const handleSelectSurvey = useCallback(
    (survey: PropertyAttributeRow) => {
      navigate(
        `/site-surveys/${urlIds.projectId}/${urlIds.propertyId}/${survey.id}`
      );
    },
    [navigate, urlIds.projectId, urlIds.propertyId]
  );

  const handleGoBack = useCallback(() => {
    if (step === "editor") {
      saveNow();
      navigate(`/site-surveys/${urlIds.projectId}/${urlIds.propertyId}`);
    } else if (step === "survey") {
      navigate(`/site-surveys/${urlIds.projectId}`);
    } else if (step === "property") {
      navigate("/site-surveys");
    }
  }, [step, navigate, urlIds, saveNow]);

  const navigateTo = useCallback(
    (targetStep: Step) => {
      if (step === "editor") saveNow();
      switch (targetStep) {
        case "project":
          navigate("/site-surveys");
          break;
        case "property":
          navigate(`/site-surveys/${urlIds.projectId}`);
          break;
        case "survey":
          navigate(
            `/site-surveys/${urlIds.projectId}/${urlIds.propertyId}`
          );
          break;
      }
    },
    [step, navigate, urlIds, saveNow]
  );

  // ── Step config ────────────────────────────────────────

  const stepConfig = {
    project: { title: "Select Project", icon: Building2, index: 0 },
    property: { title: "Select Property", icon: Home, index: 1 },
    survey: { title: "Select Site Survey", icon: ClipboardList, index: 2 },
    editor: { title: "Edit Survey", icon: ClipboardList, index: 3 },
  };
  const current = stepConfig[step];

  return (
    <div className="min-h-screen bg-background">
      {/* Top bar */}
      <header className="sticky top-0 z-50 border-b border-border/40 bg-background/95 backdrop-blur-md">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-4">
          {/* Home — back to the post-sign-in dashboard */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/dashboard")}
            className="shrink-0 -ml-2"
            aria-label="Home"
            title="Home"
          >
            <Home className="h-4 w-4" />
          </Button>

          {/* Back button */}
          {step !== "project" && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleGoBack}
              className="shrink-0 -ml-2"
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back
            </Button>
          )}

          {/* Logo / title */}
          <div className="flex items-center gap-2 shrink-0">
            <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center">
              <ClipboardList className="h-4 w-4 text-primary" />
            </div>
            <h1 className="text-lg font-semibold hidden sm:block">Site Surveys</h1>
          </div>

          {/* Breadcrumbs */}
          <nav className="flex items-center gap-1.5 text-sm overflow-hidden min-w-0 ml-auto sm:ml-4">
            {selectedProject && (
              <>
                <button
                  onClick={() => navigateTo("property")}
                  className="text-muted-foreground hover:text-foreground transition-colors truncate max-w-[120px]"
                  title={selectedProject.name}
                >
                  {selectedProject.name}
                </button>
                {selectedProperty && (
                  <>
                    <ChevronRight className="h-3 w-3 text-muted-foreground/50 shrink-0" />
                    <button
                      onClick={() => navigateTo("survey")}
                      className="text-muted-foreground hover:text-foreground transition-colors truncate max-w-[120px]"
                      title={selectedProperty.name}
                    >
                      {selectedProperty.name}
                    </button>
                    <a
                      href={withAppBasePath(`/property/${urlIds.propertyId}`)}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="shrink-0 text-muted-foreground/70 hover:text-foreground transition-colors"
                      title="Open property in new tab"
                      aria-label="Open property in new tab"
                    >
                      <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                    {selectedProperty.address && step === "survey" && (
                      <>
                        <span className="text-muted-foreground/40 shrink-0">·</span>
                        <span
                          className="text-xs text-muted-foreground/60 truncate max-w-[200px]"
                          title={[selectedProperty.address, selectedProperty.city, selectedProperty.state].filter(Boolean).join(", ")}
                        >
                          {[selectedProperty.address, selectedProperty.city, selectedProperty.state].filter(Boolean).join(", ")}
                        </span>
                      </>
                    )}
                  </>
                )}
                {selectedSurvey && step === "editor" && (
                  <>
                    <ChevronRight className="h-3 w-3 text-muted-foreground/50 shrink-0" />
                    <Badge variant="outline" className="text-xs shrink-0">
                      {new Date(selectedSurvey.captured_at).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })}
                    </Badge>
                  </>
                )}
              </>
            )}
          </nav>
        </div>
      </header>

      {/* Step indicator */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-6 pb-2">
        <div className="flex items-center gap-2 mb-6">
          {["project", "property", "survey", "editor"].map((s, i) => {
            const isActive = stepConfig[step].index === i;
            const isDone = stepConfig[step].index > i;
            return (
              <div key={s} className="flex items-center gap-2">
                {i > 0 && (
                  <div
                    className={`w-8 h-px ${isDone ? "bg-primary" : "bg-border"}`}
                  />
                )}
                <div
                  className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-medium transition-all ${
                    isActive
                      ? "bg-primary text-primary-foreground"
                      : isDone
                      ? "bg-primary/20 text-primary"
                      : "bg-muted text-muted-foreground"
                  }`}
                >
                  {i + 1}
                </div>
              </div>
            );
          })}
          <span className="text-sm font-medium ml-3">{current.title}</span>
        </div>
      </div>

      {/* Content */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 pb-12">
        {step === "project" && (
          <ProjectSelector
            projects={projects}
            loading={loading}
            onSelect={handleSelectProject}
          />
        )}

        {step === "property" && selectedProject && (
          <>
            <div className="flex justify-end gap-4 mb-1">
              <UploadTemplateButton
                kind="project"
                id={selectedProject.id}
                label="Upload Excel template (project)"
              />
              <button
                onClick={pickBulkExcelTemplate}
                className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
                title="Pick an .xlsx template and export every property's latest survey as a zip"
              >
                <FileSpreadsheet className="h-3.5 w-3.5" />
                Export Excel (all properties)
              </button>
            </div>
            <PropertySelector
              properties={selectedProject.properties}
              projectName={selectedProject.name}
              onSelect={handleSelectProperty}
              onApplied={reload}
            />
            {bulkExcelSheetDialog}
          </>
        )}

        {step === "survey" && selectedProperty && (
          <>
            <div className="flex justify-end mb-1">
              <UploadTemplateButton
                kind="property"
                id={selectedProperty.id}
                label="Upload Excel template (property)"
              />
            </div>
            <PropertyFieldsEditor
              propertyId={selectedProperty.id}
            />
            <div className="mb-6">
              <AssetsEditorSection
                projectId={urlIds.projectId!}
                propertyId={selectedProperty.id}
              />
            </div>
            <SurveySelector
              projectId={urlIds.projectId!}
              propertyId={selectedProperty.id}
              propertyName={selectedProperty.name}
              onSelect={handleSelectSurvey}
            />
          </>
        )}

        {step === "editor" && selectedSurvey && (
          <SiteSurveyEditorView
            fields={fields}
            isDirty={isDirty}
            isSaving={isSaving}
            projectId={urlIds.projectId!}
            propertyId={urlIds.propertyId!}
            surveyId={urlIds.surveyId!}
            propertyName={selectedProperty?.name ?? null}
            propertyAddress={selectedProperty?.address ?? null}
            onUpdateField={updateFieldValue}
            onUpdateFieldMetadata={updateFieldMetadata}
            onSaveNow={saveNow}
            optionsMap={optionsMap}
            requiredSet={requiredSet}
            assetsByCategory={assetsByCategory}
            templateFields={templateFields}
            excelCellMappings={excelCellMappings}
            excelDefaultSheet={excelDefaultSheet}
            capturedAt={selectedSurvey?.captured_at ?? null}
            prodData={(selectedSurvey as { prod_data?: Record<string, unknown> | null } | undefined)?.prod_data ?? null}
          />
        )}

        {/* Loading / redirecting state when URL has IDs but data hasn't loaded yet */}
        {step !== "project" && !loading && !selectedProject && (
          <div className="text-center py-12">
            <p className="text-sm text-muted-foreground">
              Project not found. Returning to selection...
            </p>
          </div>
        )}
      </main>
    </div>
  );
}
